import { ArrowRight, ShieldCheck, Zap } from "lucide-react";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/0a053d95-73ab-41d8-8498-cb7090195340/images/bba98adb48cd1e4cd74506840d1c529a06ea5686684a6e93b17d39a5a8be2d7a.png";

function HeroBackground() {
    return (
        <div className="absolute inset-0">
            <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/85 to-[#0a0a0b]/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-[#0a0a0b]/60" />
            <div className="absolute inset-0 bg-grid opacity-50" />
        </div>
    );
}

function HeroHeadline() {
    return (
        <div className="lg:col-span-8 reveal" style={{ animationDelay: "0.1s" }}>
            <h1 className="font-display font-extrabold text-[44px] sm:text-[64px] md:text-[88px] lg:text-[110px] leading-[0.92] tracking-[-0.04em]">
                Stop a fire
                <br />
                <span className="italic font-medium text-[#E53935]">before</span> it
                <br />
                starts<span className="text-[#E53935]">.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-[#a1a1aa] leading-relaxed">
                The Kitchensafe exhaust hood fire suppression system protects kitchens with a
                domestic hood and range — engineered with a two-stage heat sensor that warns the
                kitchen staff of a critical increase in temperature on the cooking surface.{" "}
                <span className="text-white">Reducing the heat will avoid an unwanted activation.</span>
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a
                    href="#pricing"
                    data-testid="hero-primary-cta"
                    className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#E53935] hover:bg-[#F44336] text-white font-bold rounded-sm transition-all"
                >
                    Get a Proposal
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                    href="#contact"
                    data-testid="hero-secondary-cta"
                    className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-white/20 hover:border-white/40 hover:bg-white/5 text-white font-medium rounded-sm transition-all"
                >
                    Talk to Tom
                </a>
            </div>
        </div>
    );
}

function Stat({ icon, label, value, sub }) {
    return (
        <div className="px-6 py-5">
            <div className="flex items-center gap-2 mb-2">
                {icon}
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#71717a]">
                    {label}
                </span>
            </div>
            <div className="font-display text-3xl font-bold tracking-tight">{value}</div>
            <div className="font-mono-tech text-[11px] text-[#a1a1aa] mt-1">{sub}</div>
        </div>
    );
}

function StatusRow({ label, value }) {
    return (
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#71717a]">
                {label}
            </span>
            <span className="font-mono-tech text-[11px] text-white">{value}</span>
        </div>
    );
}

function StatusPanel() {
    return (
        <div className="lg:col-span-4 reveal" style={{ animationDelay: "0.25s" }}>
            <div className="border border-white/10 bg-[#141416]/70 backdrop-blur-md rounded-sm">
                <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
                    <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#71717a]">
                        System Status
                    </span>
                    <span className="flex items-center gap-2 font-mono-tech text-[11px] text-[#22c55e]">
                        <span className="w-1.5 h-1.5 bg-[#22c55e] rounded-full pulse-dot" />
                        NORMAL
                    </span>
                </div>
                <div className="grid grid-cols-2 divide-x divide-white/10">
                    <Stat
                        icon={<ShieldCheck className="w-4 h-4 text-[#E53935]" />}
                        label="Standard"
                        value="UL300a"
                        sub="By Intertek"
                    />
                    <Stat
                        icon={<Zap className="w-4 h-4 text-[#F97316]" />}
                        label="Coverage"
                        value={'48"'}
                        sub="Up to 48 inch range"
                    />
                </div>
                <StatusRow label="Heat Sensor" value="2-STAGE · 175°F → ACTUATE" />
                <StatusRow label="Auto Shutoff" value="GAS · ELECTRIC" />
            </div>
        </div>
    );
}

export default function Hero() {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-screen flex items-end overflow-hidden noise"
        >
            <HeroBackground />
            <div className="absolute top-0 left-1/3 w-px h-full bg-gradient-to-b from-transparent via-[#E53935]/30 to-transparent">
                <div className="w-px h-32 bg-[#E53935] scan-line" />
            </div>

            <div className="relative max-w-[1400px] w-full mx-auto px-6 md:px-12 lg:px-16 pb-20 pt-32 md:pb-32 md:pt-40">
                <div className="flex items-center gap-3 mb-8 reveal">
                    <span className="w-2 h-2 bg-[#E53935] rounded-full pulse-dot" />
                    <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#E53935]">
                        Active Suppression · UL300a Certified
                    </span>
                </div>

                <div className="grid lg:grid-cols-12 gap-12 items-end">
                    <HeroHeadline />
                    <StatusPanel />
                </div>
            </div>
        </section>
    );
}
